import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Briefcase, CalendarCheck, Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import AppErrorState from "@/components/ui/AppErrorState";
import AppLoadingState from "@/components/ui/AppLoadingState";
import { getApiErrorMessage } from "@/lib/api";
import apiClient from "@/lib/api";
import { getRecruitmentWorkflowDashboardStats } from "@/lib/recruitmentApi";
import { formatNumber } from "@/lib/pilotageUi";
import NotificationsDrawer from "@/components/admin/recruitment/NotificationsDrawer";
import NeedCreateModal from "@/components/admin/recruitment/NeedCreateModal";
import CandidateCreateModal from "@/components/admin/recruitment/CandidateCreateModal";

const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color,
  link,
  linkText,
  testId,
  onClick,
}) => (
  <div
    className="glass rounded-2xl p-5 border border-white/10 hover:border-white/20 transition-all flex flex-col justify-between"
    data-testid={testId}
  >
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</p>
        <p className="mt-2 text-3xl font-extrabold text-white tracking-tight">{value}</p>
        {subtitle ? (
          <p className="mt-1 text-xs text-slate-400 line-clamp-1">{subtitle}</p>
        ) : null}
      </div>
      <div className={`rounded-xl p-3 shrink-0 shadow-inner ${color}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
    </div>
    {link ? (
      <Link
        to={link}
        className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-400 transition-colors hover:text-blue-300"
      >
        <span>{linkText || "Accéder"}</span>
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    ) : onClick ? (
      <button
        type="button"
        onClick={onClick}
        className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-400 transition-colors hover:text-blue-300 text-left"
      >
        <span>{linkText || "Ouvrir"}</span>
        <ArrowRight className="h-3.5 w-3.5" />
      </button>
    ) : null}
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifDrawerOpen, setNotifDrawerOpen] = useState(false);

  // Modals for Actions A & B
  const [showNeedModal, setShowNeedModal] = useState(false);
  const [showCandidateModal, setShowCandidateModal] = useState(false);


  const fetchStats = async () => {
    setLoading(true);
    setError(null);

    try {
      const nextStats = await getRecruitmentWorkflowDashboardStats();
      setStats(nextStats);
    } catch (requestError) {
      setError(
        getApiErrorMessage(
          requestError,
          "Erreur lors du chargement des statistiques du recrutement",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchStats();
  }, []);

  const handleCreateNeed = async (payload) => {
    const res = await apiClient.post("/recruitment/needs", payload);
    return res.data;
  };

  if (loading) {
    return (
      <AppLoadingState
        title="Chargement du tableau de bord recrutement"
        description="Calcul en temps réel des indicateurs de staffing..."
        testId="recruitment-dashboard-loading"
      />
    );
  }

  if (error) {
    return (
      <AppErrorState
        title="Tableau de bord recrutement indisponible"
        message={error}
        hint="Les autres rubriques de recrutement restent accessibles depuis le menu."
        onAction={fetchStats}
        testId="recruitment-dashboard-error"
      />
    );
  }

  const s = stats || {};
  const activeNeeds = Number(s.active_needs ?? 0);
  const ongoingApps = Number(s.ongoing_applications ?? 0);
  const pendingRh = Number(s.pending_rh ?? 0);
  const pendingTech = Number(s.pending_tech ?? 0);
  const pendingCommercial = Number(s.pending_commercial ?? 0);
  const clientPresentations = Number(s.client_presentations ?? 0);
  const upcomingInterviews = Number(s.upcoming_interviews ?? 0);
  const pendingDecisions = Number(s.pending_decisions ?? 0);
  const activeCvInvitations = Number(s.active_cv_invitations ?? 0);
  const unreadNotifs = Number(s.unread_notifications ?? 0);

  const actions = [
    [pendingRh, "Qualifications RH à effectuer", "?stage=qualification"],
    [pendingTech, "Avis techniques attendus", "?validation=tech"],
    [pendingCommercial, "Validations commerciales attendues", "?validation=commercial"],
    [pendingDecisions, "Décisions à enregistrer", "?stage=decision"],
  ].filter(([count]) => count > 0);

  return (
    <div className="space-y-6 pb-10" data-testid="recruitment-dashboard">
      <section className="rounded-2xl border border-white/10 bg-slate-900/70 p-6 space-y-5">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Tableau de bord Recrutement</h1>
          <p className="mt-2 text-slate-300">Que dois-je faire maintenant ?</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => setShowNeedModal(true)} data-testid="action-new-need-button">+ Nouveau besoin client</Button>
          <Button onClick={() => setShowCandidateModal(true)} variant="outline" data-testid="action-add-candidate-button">+ Ajouter un candidat</Button>
        </div>
      </section>

      <section aria-labelledby="ongoing-heading" className="space-y-3">
        <h2 id="ongoing-heading" className="text-lg font-bold text-white">Quels recrutements sont en cours ?</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard title="Besoins actifs" value={formatNumber(activeNeeds)} icon={Briefcase} color="bg-blue-600/40" link="/admin/recrutement/besoins" linkText="Voir les besoins" testId="recruitment-kpi-active-needs" />
          <StatCard title="Candidatures en cours" value={formatNumber(ongoingApps)} icon={Users} color="bg-blue-600/40" link="/admin/recrutement/candidatures" linkText="Voir le pipeline" testId="recruitment-kpi-ongoing-applications" />
          <StatCard title="Entretiens à venir" value={formatNumber(upcomingInterviews)} icon={CalendarCheck} color="bg-blue-600/40" link="/admin/recrutement/entretiens" linkText="Voir le planning" testId="recruitment-kpi-upcoming-interviews" />
        </div>
      </section>

      <section className="rounded-2xl border border-white/10 bg-slate-900/50 p-5 space-y-3" aria-labelledby="actions-heading">
        <h2 id="actions-heading" className="text-lg font-bold text-white">Quelles actions attendent mon intervention ?</h2>
        {actions.length ? <ul className="divide-y divide-white/10">
          {actions.map(([count, label, query]) => <li key={query}>
            <Link to={`/admin/recrutement/candidatures${query}`} className="flex items-center justify-between gap-3 min-h-12 py-3 text-slate-200 hover:text-blue-300">
              <span>{label}</span><span className="font-bold">{count} →</span>
            </Link>
          </li>)}
        </ul> : <p className="text-slate-400">Aucune action en attente. Créez un besoin ou ajoutez un candidat pour commencer.</p>}
      </section>

      <details className="rounded-xl border border-white/10 p-5 text-slate-300">
        <summary className="cursor-pointer min-h-11 font-medium">Autres informations et accès</summary>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 text-sm">
          <Link to="/admin/recrutement/candidatures?stage=presente">Présentations clients : {clientPresentations}</Link>
          <Link to="/admin/recrutement/candidats?has_active_invitation=true">Invitations CV actives : {activeCvInvitations}</Link>
          <button className="text-left" onClick={() => setNotifDrawerOpen(true)}>Notifications non lues : {unreadNotifs}</button>
          <Link to="/admin/recrutement/vivier">Vivier de talents</Link>
          <Link to="/admin/recrutement/cv-library">Bibliothèque CVs</Link>
          <button onClick={fetchStats} className="text-left" data-testid="recruitment-dashboard-refresh-button">Actualiser les indicateurs</button>
        </div>
      </details>

      {/* Action A: Need Create Modal */}
      <NeedCreateModal
        isOpen={showNeedModal}
        onClose={() => setShowNeedModal(false)}
        onCreateNeed={handleCreateNeed}
        onCreated={(created) => {
          const id = created?._id || created?.id;
          if (id) {
            navigate(`/admin/recrutement/besoins/${id}`);
          }
        }}
      />

      {/* Action B: Candidate Create Modal */}
      <CandidateCreateModal
        isOpen={showCandidateModal}
        onClose={() => setShowCandidateModal(false)}
        onCandidateCreated={() => {
          void fetchStats();
        }}
      />

      {/* Notifications Drawer */}
      <NotificationsDrawer
        isOpen={notifDrawerOpen}
        onClose={() => {
          setNotifDrawerOpen(false);
          void fetchStats();
        }}
        onSelectNotification={(notif) => {
          setNotifDrawerOpen(false);
          if (notif?.metadata?.need_id) {
            navigate(`/admin/recrutement/besoins/${notif.metadata.need_id}`);
          } else if (notif?.metadata?.candidate_id) {
            navigate(`/admin/recrutement/candidats/${notif.metadata.candidate_id}`);
          } else if (notif?.metadata?.application_id) {
            navigate(`/admin/recrutement/candidatures?application_id=${notif.metadata.application_id}`);
          } else {
            navigate("/admin/recrutement/candidatures");
          }
        }}
        onSelectApplication={(appId) => {
          setNotifDrawerOpen(false);
          if (appId) {
            navigate(`/admin/recrutement/candidatures?application_id=${appId}`);
          } else {
            navigate("/admin/recrutement/candidatures");
          }
        }}
      />
    </div>
  );
};

export default AdminDashboard;
